import React, { useState } from 'react'
import PropTypes from 'prop-types'
import glamorous from 'glamorous'
import { css } from 'glamor'
import getContent from 'pp-react-l10n'
import {
  Pagination,
  v2white,
  v2neutral100,
  v2blue400,
} from '@paypalcorp/pp-react'
import {
  phoneLandscapeMaxAndSmaller,
  phoneLandscapeMinAndSmaller,
} from 'lib/styles/media-queries'

const l10n = getContent('pages/shops/common')

const heights = {
  desktop: '350px',
  phoneMax: '224px',
  phoneMin: '200px',
}

const Container = glamorous.div({
  height: `${heights.desktop}`,
  width: '100%',
  position: 'relative',
  [phoneLandscapeMaxAndSmaller]: {
    height: `${heights.phoneMax}`,
  },
  [phoneLandscapeMinAndSmaller]: {
    height: `${heights.phoneMin}`,
  },
})

const ContentBlock = glamorous.div({
  height: `${heights.desktop}`,
  width: '100%',
  top: '-10%',
  left: '50%',
  transform: 'translateX(-50%)',
  minWidth: '700px',
  overflow: 'hidden',
  position: 'absolute',
  [phoneLandscapeMaxAndSmaller]: {
    height: `${heights.phoneMax}`,
  },
  [phoneLandscapeMinAndSmaller]: {
    height: `${heights.phoneMin}`,
  },
})

const BackgroundPhoto = glamorous.div(({ image }) => ({
  position: 'absolute',
  boxSizing: 'borderBox',
  width: '100%',
  minWidth: '700px',
  height: `${heights.desktop}`,
  left: '50%',
  transform: 'translateX(-50%)',
  backgroundImage: `url(${encodeURI(image)})`,
  backgroundRepeat: 'no-repeat',
  backgroundSize: 'cover',
  filter: 'brightness(40%) blur(3px)',
  [phoneLandscapeMaxAndSmaller]: {
    minWidth: '131%',
    height: `${heights.phoneMax}`,
  },
  [phoneLandscapeMinAndSmaller]: {
    minWidth: 'calc(100% + 41px)',
    height: `${heights.phoneMin}`,
  },
}))

const TopPhoto = glamorous.div(({ image }) => ({
  backgroundImage: `url(${encodeURI(image)})`,
  display: 'block',
  position: 'absolute',
  top: '50%',
  left: '75%',
  zIndex: '10',
  width: '100%',
  height: `${heights.desktop}`,
  backgroundSize: 'contain',
  backgroundRepeat: 'no-repeat',
  transform: 'translate(-50%, -50%)',
  ['@media (max-width: 640px)']: {
    minWidth: '131%',
    height: `(${heights.phoneMax})`,
    left: '90%',
  },
  ['@media (max-width: 481px)']: {
    minWidth: '131%',
    height: `${heights.phoneMax}`,
    left: '100%',
  },
  ['@media (max-width: 281px)']: {
    minWidth: 'calc(100% + 21px)',
    height: `${heights.phoneMax}`,
    left: '85%',
  },
}))

const PaginationContainer = css({
  position: 'absolute',
  bottom: '14px',
  width: '100%',
  display: 'flex',
  justifyContent: 'center',
})

const Page = css({
  height: '6px',
  width: '6px',
  borderRadius: '100%',
  backgroundColor: v2neutral100,
  margin: '0 4px',
  color: v2white,
  fontSize: 0,
  ':hover': {
    color: v2white,
    textDecoration: 'none',
  },
})

const ActivePage = css({
  backgroundColor: v2blue400,
  color: v2white,
  transition: 'none',
})

const PageArrow = {
  height: '40px',
  width: '40px',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  color: v2white,
  background: 'rgba(12, 12, 13, 0.3)',
  fontWeight: 100,
  fontSize: '1.25rem',
  position: 'absolute',
  zIndex: 5,
  top: `calc(-${heights.desktop} / 2)`,
  ':hover': {
    background: 'rgba(12, 12, 13, 0.5)',
  },
  [phoneLandscapeMaxAndSmaller]: {
    top: `calc(-${heights.phoneMax} / 2)`,
  },
  [phoneLandscapeMinAndSmaller]: {
    top: `calc(-${heights.phoneMin} / 2)`,
  },
}

const PageArrowLeft = {
  left: '-18%',
  [phoneLandscapeMaxAndSmaller]: {
    left: '-10%',
  },
  [phoneLandscapeMinAndSmaller]: {
    left: '-8px',
  },
}

const PageArrowRight = {
  right: '-18%',
  [phoneLandscapeMaxAndSmaller]: {
    right: '-10%',
  },
  [phoneLandscapeMinAndSmaller]: {
    right: '-8px',
  },
}

const PageSpace = css({
  display: 'none',
})

ProductCoverPhoto.propTypes = {
  productPhotos: PropTypes.arrayOf(
    PropTypes.shape({
      imageUrl: PropTypes.string.isRequired,
    }),
  ),
}

function ProductCoverPhoto(props) {
  const [currentPage, setCurrentPage] = useState(0)
  const { productPhotos } = props

  const handleChange = page => {
    setCurrentPage(page - 1)
  }

  const productPhotoCount = productPhotos && productPhotos.length

  return (
    <Container>
      {productPhotoCount && (
        <ContentBlock>
          <BackgroundPhoto
            image={productPhotos[currentPage].imageUrl}
            data-test-id="shops-product-photos"
          />
          <TopPhoto
            image={productPhotos[currentPage].imageUrl}
            data-test-id="shops-product-photos"
          />
          <Pagination
            totalPageCount={productPhotoCount}
            currentPage={currentPage + 1}
            onChange={handleChange}
            prevPageLabel={l10n('prevPageLabel')}
            nextPageLabel={l10n('nextPageLabel')}
            paginationLabel={l10n('paginationLabel')}
            className={`${PaginationContainer}`}
            pageClassName={`${Page}`}
            activePageClassName={`${ActivePage}`}
            arrowLeftClassName={`${css(PageArrow, PageArrowLeft)}`}
            arrowRightClassName={`${css(PageArrow, PageArrowRight)}`}
            arrowSpaceClassName={`${PageSpace}`}
          />
        </ContentBlock>
      )}
    </Container>
  )
}

export default ProductCoverPhoto
